import { useState } from 'react';
import type { ChangeRequest, GridColumn, GridRow } from '../types';
import { useT } from '../i18n';

interface ChangeRequestReviewProps {
  cr: ChangeRequest;
  domains: GridColumn[];
  levels: GridRow[];
  pipelineColumns: GridColumn[];
  onApprove: (comment: string, forceApply: boolean) => Promise<void>;
  onReject: (comment: string) => Promise<void>;
  onClose: () => void;
}

type TFunc = (key: string, params?: Record<string, string | number>) => string;

function parseJSON<T>(val: any, fallback: T): T {
  if (!val) return fallback;
  if (typeof val === 'object') return val;
  try { return JSON.parse(val); } catch { return fallback; }
}

function resolveLabel(id: any, items: { id: number; label: string }[], t: TFunc): string {
  if (id == null) return t('changeReq.emptyValue');
  const item = items.find(i => i.id === Number(id));
  return item?.label || String(id);
}

function formatField(key: string, value: any, t: TFunc): string {
  if (value == null || value === '') return t('changeReq.emptyValue');
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
}

function fieldLabel(key: string, t: TFunc): string {
  switch (key) {
    case 'name': return t('changeReq.field.name');
    case 'problem': return t('changeReq.field.problem');
    case 'goal': return t('changeReq.field.goal');
    case 'value_added': return t('changeReq.field.valueAdded');
    case 'deliverables': return t('changeReq.field.deliverables');
    case 'resources_effort': return t('changeReq.field.resourcesEffort');
    case 'ownership': return t('changeReq.field.ownership');
    case 'dependencies': return t('changeReq.field.dependencies');
    case 'domain_id': return t('changeReq.field.domain');
    case 'level_id': return t('changeReq.field.level');
    case 'status_id': return t('changeReq.field.status');
    case 'prequalification': return t('changeReq.field.prequalification');
    case 'scoping': return t('changeReq.field.scoping');
    case 'planning': return t('changeReq.field.planning');
    case 'execution': return t('changeReq.field.execution');
    case 'handover': return t('changeReq.field.handover');
    case 'deferred_review': return t('changeReq.field.deferredReview');
    case 'rcajo': return 'RCAJO';
    case 'sequencing': return t('changeReq.field.sequencing');
    default: return key;
  }
}

export function ChangeRequestReview({ cr, domains, levels, pipelineColumns, onApprove, onReject, onClose }: ChangeRequestReviewProps) {
  const { t } = useT();
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isStale = cr.project_current_updated_at && cr.project_version_at &&
    cr.project_current_updated_at !== cr.project_version_at;

  const computeDiff = () => {
    const snapshot = cr.snapshot_before;
    const proposed = cr.proposed_changes;
    const diffs: { field: string; label: string; oldVal: string; newVal: string }[] = [];

    // Must cover the same field set the approve handler applies, incl. the
    // stage-model JSON columns — otherwise plan/scoping edits show "no changes".
    const checkFields = ['name', 'problem', 'goal', 'value_added', 'deliverables', 'resources_effort', 'ownership', 'dependencies', 'domain_id', 'level_id', 'status_id', 'prequalification', 'scoping', 'planning', 'execution', 'handover', 'deferred_review', 'rcajo', 'sequencing'];

    for (const key of checkFields) {
      const oldRaw = snapshot[key];
      const newRaw = proposed[key];

      // Skip if both are null/undefined/empty
      if ((oldRaw == null || oldRaw === '') && (newRaw == null || newRaw === '')) continue;

      let oldStr: string;
      let newStr: string;

      if (key === 'domain_id') {
        oldStr = resolveLabel(oldRaw, domains, t);
        newStr = resolveLabel(newRaw, domains, t);
      } else if (key === 'level_id') {
        oldStr = resolveLabel(oldRaw, levels, t);
        newStr = resolveLabel(newRaw, levels, t);
      } else if (key === 'status_id') {
        oldStr = resolveLabel(oldRaw, pipelineColumns, t);
        newStr = resolveLabel(newRaw, pipelineColumns, t);
      } else {
        oldStr = formatField(key, typeof oldRaw === 'string' ? parseJSON(oldRaw, oldRaw) : oldRaw, t);
        newStr = formatField(key, typeof newRaw === 'string' ? parseJSON(newRaw, newRaw) : newRaw, t);
      }

      if (oldStr !== newStr) {
        diffs.push({ field: key, label: fieldLabel(key, t), oldVal: oldStr, newVal: newStr });
      }
    }

    return diffs;
  };

  const diffs = computeDiff();

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await onApprove(comment, !!isStale);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      await onReject(comment);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cr-review">
      <div className="cr-review-header">
        <h3>{cr.project_name || t('changeReq.projectFallback')} {cr.project_code && <span className="text-ink-3">({cr.project_code})</span>}</h3>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>{t('common.close')}</button>
      </div>

      <div className="cr-review-meta">
        <span>{t('changeReq.requestedByLabel')} <strong>{cr.requester_name}</strong></span>
        <span>{new Date(cr.created_at).toLocaleString()}</span>
      </div>

      {isStale && (
        <div className="staleness-warning">
          {t('changeReq.stalenessWarning')}
        </div>
      )}

      {diffs.length === 0 ? (
        <div className="cr-no-changes">{t('changeReq.noDifferences')}</div>
      ) : (
        <div className="cr-diff-list">
          {diffs.map((d) => (
            <div key={d.field} className="diff-entry">
              <div className="diff-field-label">{d.label}</div>
              <div className="diff-values">
                <div className="diff-old">
                  <span className="diff-label">{t('changeReq.current')}</span>
                  <pre>{d.oldVal}</pre>
                </div>
                <div className="diff-new">
                  <span className="diff-label">{t('changeReq.proposed')}</span>
                  <pre>{d.newVal}</pre>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="cr-review-actions">
        <textarea
          className="form-input--bare"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t('changeReq.commentPlaceholder')}
          rows={2}
        />
        <div className="cr-review-buttons">
          <button
            className="btn btn-ghost"
            style={{ color: 'var(--err)' }}
            onClick={handleReject}
            disabled={submitting}
          >
            {t('changeReq.reject')}
          </button>
          <button
            className="btn btn-accent"
            onClick={handleApprove}
            disabled={submitting}
          >
            {isStale ? t('changeReq.forceApprove') : t('changeReq.approve')}
          </button>
        </div>
      </div>
    </div>
  );
}
